import { pick } from "ramda";
import { EventChannel, eventChannel } from "redux-saga";
import { call, put, take } from "redux-saga/effects";

import firebase from "../../init/firebase";
import { UserRole } from "../../types/user.types";
import { academyDocRef, brandDocRef, coachDocRef, sportClubDocRef } from "../../utils/referencies";
import { loadingUser, logout, setUserData } from "../reducers/user";

const getAuthChannel = (): EventChannel<any> => {
  return eventChannel((emitter) => {
    return firebase?.auth().onAuthStateChanged(
      (user) => emitter({ user }),
      (ex: any) => console.log(ex.message)
    );
  });
};

function* getClubInfo(clubId: string): Record<string, any> {
  const snapshot = yield sportClubDocRef(clubId).get();
  return snapshot.exists ? { reservationLastSeenAt: snapshot.data().reservationLastSeenAt.toDate() } : {};
}

function* getStoreInfo(brandId: string): Record<string, any> {
  const snapshot = yield brandDocRef(brandId).get();
  return snapshot.exists
    ? {
        status: snapshot.data().status,
        ordersLastSeenAt: snapshot.data().ordersLastSeenAt.toDate(),
      }
    : {};
}

function* getCoachInfo(coachId: string): Record<string, any> {
  const snapshot = yield coachDocRef(coachId).get();
  return snapshot.exists
    ? {
        defaultSlotTime: snapshot.data().defaultSlotTime,
        reservationLastSeenAt: snapshot.data().reservationLastSeenAt.toDate(),
        sportTypeIds: snapshot.data().sportTypeIds,
      }
    : {};
}

function* getAcademyInfo(academyId: string): Record<string, any> {
  const snapshot = yield academyDocRef(academyId).get();
  return snapshot.exists ? { sportTypeIds: snapshot.data().sportTypeIds } : {};
}

export default function* watchForFirebaseAuth(): any {
  try {
    yield put(loadingUser(true));

    const authChannel = yield call(getAuthChannel);
    while (true) {
      const res = yield take(authChannel);
      if (res.user) {
        const idTokenResult = yield res.user.getIdTokenResult();
        const permissions = pick<any>(["role", "ref"])(idTokenResult.claims);
        const data: any = pick(["email", "emailVerified", "displayName"])(res.user);

        let currentUserData = {};

        if (permissions.role === UserRole.sportClub) {
          currentUserData = yield getClubInfo(res.user.uid);
        }
        if (permissions.role === UserRole.store) {
          currentUserData = yield getStoreInfo(res.user.uid);
        }
        if (permissions.role === UserRole.coach) {
          currentUserData = yield getCoachInfo(res.user.uid);
        }
        if (permissions.role === UserRole.academy) {
          currentUserData = yield getAcademyInfo(res.user.uid);
        }

        yield put(
          setUserData({
            ...data,
            id: res.user.uid,
            path: permissions.ref,
            creationTime: res.user.metadata ? new Date(res.user.metadata.creationTime) : null,
            ...permissions,
            ...currentUserData,
          })
        );
      } else yield put(logout());

      yield put(loadingUser(false));
    }
  } catch (ex: any) {
    console.log(ex.message);
    yield put(loadingUser(false));
    firebase
      .auth()
      .signOut()
      .then(() => {
        console.log("logged out");
      })
      .catch((ex) => {
        console.log(ex.message);
      });
  }
}
